import React, { useMemo } from 'react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { useRoleAwareLayout } from '../../../hooks/responsive/useResponsive';
import ReadingProvider from '../../../contexts/ReadingContext';
import ParentDashboardContent from './ParentDashboardContent';
import KidDashboardContent from './KidDashboardContent';
import DashboardSection from '../shared/DashboardSection';

import SmartDashboard from '../SmartDashboard';
import MobileDashboard from '../MobileDashboard';

const DashboardContent = ({ className = '', ...props }) => {
  const { user, family, currentUser } = useFamilyContext();
  const { userRole, isMobile, breakpoint } = useRoleAwareLayout();
  
  
  // Memoize content to prevent unnecessary re-renders
  const ContentComponent = useMemo(() => {
    const contentProps = {
      user,
      family,
      isMobile,
      breakpoint,
      ...props
    };
    
    const dashboardComponent = isMobile ? <MobileDashboard {...contentProps} /> : <SmartDashboard {...contentProps} />;
    
    switch (userRole) {
      case 'parent':
        // Use SmartDashboard for parents (no reading context needed)
        return dashboardComponent;
      case 'kid':
        // Wrap kids dashboard with ReadingProvider for reading challenge
        return (
          <ReadingProvider>
            {dashboardComponent}
          </ReadingProvider>
        );
      default:
        console.warn(`Unknown user role: ${userRole}`);
        return <DefaultDashboardContent {...contentProps} />;
    }
  }, [userRole, user, family, isMobile, breakpoint, props]);
  
  const containerClasses = [
    'dashboard-content',
    `dashboard-content--${userRole}`,
    `dashboard-content--${breakpoint}`,
    className
  ].filter(Boolean).join(' ');
  
  return (
    <div className={containerClasses}>
      {ContentComponent}
    </div>
  );
};

// Fallback for unknown roles
const DefaultDashboardContent = ({ user, isMobile }) => (
  <DashboardSection title="Welcome" variant="default">
    <div className="text-center py-8">
      <p className="text-slate-600 mb-4">Welcome {user?.name || 'User'}!</p>
      <p className="text-sm text-stone-500">
        🆕 New modular dashboard architecture is active
      </p>
      <p className="text-xs text-stone-400 mt-2">
        Please contact support if you're seeing this message.
      </p>
    </div>
  </DashboardSection>
);

export default DashboardContent;